import React, { useState } from 'react';
import { Col, Row, Skeleton, Space, Typography } from 'antd';
import dayjs from 'dayjs';
import { DateRangeFilter, DateRange, defaultDateRange } from '@shared/components/DateRangeFilter';
import { LineChartCard } from '@shared/components/charts/LineChartCard';
import { BarChartCard } from '@shared/components/charts/BarChartCard';
import { useAnalyticsAudience } from '@shared/api/hooks/useAnalyticsAudience';

export function AudiencePage(): React.ReactElement {
  const [range, setRange] = useState<DateRange>(defaultDateRange());
  const { data, isLoading } = useAnalyticsAudience(range);

  const registrations = (data?.registrations ?? []).map((r) => ({
    ...r,
    date: dayjs(r.date).format('DD/MM'),
  }));
  const activeUsers = (data?.activeUsers ?? []).map((r) => ({
    ...r,
    date: dayjs(r.date).format('DD/MM'),
  }));

  // T-144 — sempre 3 barre, incluso "Sconosciuto": e' il bucket di chi non ha
  // ancora comunicato l'OS. Non lo nascondiamo ne' lo spalmiamo su iOS/Android.
  const pb = data?.platformBreakdown ?? { ios: 0, android: 0, unknown: 0 };
  const platformData = [
    { label: 'iOS', tifosi: pb.ios },
    { label: 'Android', tifosi: pb.android },
    { label: 'Sconosciuto', tifosi: pb.unknown },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <Typography.Title level={3} style={{ margin: 0, fontStyle: 'italic' }}>
            Pubblico
          </Typography.Title>
          <Typography.Text type="secondary">Registrazioni e utenti attivi nel tempo</Typography.Text>
        </div>
        <DateRangeFilter value={range} onChange={setRange} />
      </Space>

      {isLoading ? (
        <Skeleton active />
      ) : (
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={12}>
            <LineChartCard
              title="Nuove registrazioni"
              data={registrations}
              xKey="date"
              series={[{ key: 'newUsers', label: 'Nuovi tifosi' }]}
            />
          </Col>
          <Col xs={24} lg={12}>
            <LineChartCard
              title="Utenti attivi"
              data={activeUsers}
              xKey="date"
              series={[{ key: 'count', label: 'Attivi' }]}
            />
          </Col>
          <Col xs={24} lg={12}>
            <BarChartCard
              title="Ultima piattaforma vista"
              data={platformData}
              xKey="label"
              series={[{ key: 'tifosi', label: 'Tifosi' }]}
            />
            <Typography.Text
              type="secondary"
              style={{ display: 'block', marginTop: 8, fontSize: 12 }}
            >
              Ultima piattaforma con cui il tifoso ha aperto l&apos;app (non un conteggio di
              dispositivi). Il dato si popola solo in avanti: chi non ha ancora aperto l&apos;app
              aggiornata dopo il rilascio risulta «Sconosciuto», e non ne stimiamo la piattaforma.
            </Typography.Text>
          </Col>
        </Row>
      )}
    </div>
  );
}
